import { Autocomplete, AutocompleteOption, ListItemContent, ListItemDecorator } from '@mui/joy';
import { useMemo } from 'react';

import { type AutocompleteOption as Option } from '~/common/types';

import { DivisionKeyIcon, InlineSpinner } from '@/shared/icons/Icons';
import FormField, { type FieldProps } from '../input/FormField';
import NoWrapListBoxAdapter from '../adapters/NoWrapListBoxAdapter';
import { trpc } from '@/config/trpc';

type SelectDivisionKeyProps = FieldProps &
  React.PropsWithChildren<{
    homeownerAssociationId: string | null;
    value: string | null;
    disabled?: boolean;
    onChange: (option: Option | null) => void;
    divisionKeysToExclude?: string[];
    excludeDeferredKeys?: boolean;
  }>;

const SelectDivisionKey: React.FC<SelectDivisionKeyProps> = ({
  homeownerAssociationId,
  value,
  onChange,
  divisionKeysToExclude,
  excludeDeferredKeys = false,
  children,
  disabled = false,
  ...fieldProps
}) => {
  const {
    data: divisionKeysAutocomplete,
    isLoading: isHomeownerAssociationDivisionKeysAutocompleteLoading,
  } = trpc.hoa.divisionKey.autocomplete.useQuery(
    {
      allowDeferredTypes: !excludeDeferredKeys,
      id: homeownerAssociationId as string,
    },
    {
      enabled: !!homeownerAssociationId,
    },
  );

  const options = useMemo(
    () =>
      divisionKeysAutocomplete?.filter((key) => !divisionKeysToExclude?.includes(key.value)) ?? [],
    [divisionKeysAutocomplete, divisionKeysToExclude],
  );

  return (
    <FormField {...fieldProps}>
      <div className="flex gap-2">
        <Autocomplete
          className="flex-1"
          disabled={disabled}
          loading={isHomeownerAssociationDivisionKeysAutocompleteLoading}
          options={options}
          slots={{
            listbox: NoWrapListBoxAdapter,
          }}
          startDecorator={
            isHomeownerAssociationDivisionKeysAutocompleteLoading ? (
              <InlineSpinner />
            ) : (
              <DivisionKeyIcon />
            )
          }
          value={divisionKeysAutocomplete?.find((key) => key.value === value) ?? null}
          getOptionLabel={(opt) => opt?.label ?? opt}
          onChange={(_, option) => onChange(option)}
          renderOption={(props, option) => (
            <AutocompleteOption {...props} key={option.value}>
              <ListItemDecorator>
                <DivisionKeyIcon />
              </ListItemDecorator>
              <ListItemContent>{option.label}</ListItemContent>
            </AutocompleteOption>
          )}
        />
        {children}
      </div>
    </FormField>
  );
};

export default SelectDivisionKey;
